/**
 * Constrained preset 3D options — website-builder look, Mesh3D sync,
 * cart payload matching Thinkspace RING-BUILDER properties.
 *
 * Sections: boot → style/parts → gems/patterns → UI → viewer → cart
 */
(() => {
  /* -------------------------------------------------------------------------- */
  /* Boot: mount node + recipe / catalog JSON                                   */
  /* -------------------------------------------------------------------------- */
  const root = document.querySelector('[data-preset-3d]');
  if (!root) return;

  const catalogEl = root.querySelector('[data-preset-3d-catalog]');
  const recipeEl = root.querySelector('[data-preset-3d-recipe]');
  if (!catalogEl || !recipeEl) return;

  let catalog;
  let recipe;
  try {
    catalog = JSON.parse(catalogEl.textContent);
    recipe = JSON.parse(recipeEl.textContent);
  } catch (e) {
    console.error('[preset-3d] bad JSON', e);
    return;
  }

  const money = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

  const portalVersion =
    root.dataset.portalVersion ||
    new URL(window.location.href).searchParams.get('portal_version') ||
    '2';
  const PART2_PREFIX = portalVersion === '2' ? 'flat' : 'matte';
  const BASE =
    portalVersion === '2'
      ? 'https://portal.mesh3d.com/embed/186f48af-e85c-4bfa-96b0-46b4a9b520c2?client=sanjose'
      : 'https://app.mesh3d.com/viewer/?client=sanjose';

  /* -------------------------------------------------------------------------- */
  /* Style / part resolution (specialty: 71 + SIG waive band; 71 → L)           */
  /* -------------------------------------------------------------------------- */
  const topCode = recipe.top_code || root.dataset.topCode || '245F';
  /** Fierce (71) + Signet (SIG): total rings — no separate band part (kiosk / Thinkspace). */
  const TOP_STYLE_WAIVES_BAND = new Set(['71', 'SIG', 'SIGNET']);
  /**
   * Mesh3D specialty size locks (catalog may list more sizes than portal has).
   * Fierce uses live-builder sizes N/E/L — do not force 12x10.
   */
  const STYLE_FORCED_SHAPES = {};

  const styleMap = Object.fromEntries(catalog.style_codes || []);
  const styleMeta = styleMap[topCode] || {
    label: topCode,
    gemstone_groups_count: 0,
    has_stones: false,
    shapes: ['N', 'E', 'L'],
    bands: [],
  };

  function styleWaivesBand(code, meta) {
    const tok = String(code || '')
      .trim()
      .toUpperCase()
      .replace(/^SIGNET$/, 'SIG');
    if (TOP_STYLE_WAIVES_BAND.has(tok)) return true;
    if (
      recipe.waive_band === true ||
      recipe.waive_band === 'true' ||
      root.dataset.waiveBand === 'true'
    ) {
      return true;
    }
    return Array.isArray(meta?.bands) && meta.bands.length === 0;
  }

  function resolveShapeCode(requested, meta) {
    const forced = STYLE_FORCED_SHAPES[topCode];
    const catalogShapes = Array.isArray(meta?.shapes) && meta.shapes.length
      ? meta.shapes
      : ['N', 'E', 'L'];
    const allowed = forced || catalogShapes;
    if (allowed.includes(requested)) return requested;
    for (const pref of ['L', 'N', 'E']) {
      if (allowed.includes(pref)) return pref;
    }
    return allowed[0] || 'N';
  }

  function resolveBandCode(requested, meta, waives) {
    if (waives) return '';
    const allowed = Array.isArray(meta?.bands) ? meta.bands : [];
    if (!allowed.length) return requested || 'SD';
    if (allowed.includes(requested)) return requested;
    return allowed[0] || '';
  }

  const waivesBand = styleWaivesBand(topCode, styleMeta);
  let shapeCode = resolveShapeCode(
    recipe.shape_code || root.dataset.shapeCode || 'N',
    styleMeta
  );
  let bandCode = resolveBandCode(
    recipe.band_code || root.dataset.bandCode || 'SD',
    styleMeta,
    waivesBand
  );
  const schoolName = recipe.school_name || root.dataset.schoolName || '';
  const builderVariantId = String(
    recipe.builder_variant_id || root.dataset.builderVariantId || ''
  );

  const metalColors = catalog.metal_colors || [];
  const gemColors = catalog.gemstone_colors || [];
  const ringSizes = catalog.ring_sizes || [];
  const engravingPrice = parseFloat(catalog.addons_pricing?.engraving || '60') || 60;
  let engravingWaived = false;
  let engravingCodeStatus = '';
  let engravingCodeTicket = 0;
  /**
   * Same waiver hashes as sj-engraving-enhancer.js. The page stores hashes, not the codes.
   * To change one:
   *   node -e "const c=require('crypto');console.log(c.createHash('sha256').update(String(process.argv[1]).trim().toUpperCase()).digest('hex'))" "New Code Here"
   */
  const ENGRAVING_WAIVER_SHA256 = [
    '5d6f8760bc0c738ddbaf3c36d8add8ad253e50c847422c6f5a840f2c73c040ef',
    'fc05455f9acd09eb2578527f65554b93e517d74dfe8ffaf79dbd996773b87291',
  ];
  const twoTonePrice = parseFloat(catalog.addons_pricing?.two_tone || '275') || 275;
  const twoToneBakedIntoOfficial =
    recipe.two_tone === true ||
    recipe.two_tone === 'true' ||
    recipe.two_tone_in_price === true ||
    root.dataset.twoToneInPrice === 'true';
  function applyTwoToneToTotal(total, twoTone, twoToneOk) {
    // Official variants on two-tone presets already include +$275.
    // Star cannot be two-tone and stays sheet-only.
    if (twoToneBakedIntoOfficial && twoToneOk) {
      if (!twoTone) return total - twoTonePrice;
      return total;
    }
    if (twoTone) return total + twoTonePrice;
    return total;
  }
  const sealSurchargeMap =
    catalog.addons_pricing?.seal_color_additional_price || {};

  /** True when the top can take accent gem colors (ruby/etc.), not just white CZ/Moi. */
  const hasTopStones =
    styleMeta.has_stones === true &&
    Number(styleMeta.gemstone_groups_count || 0) > 0;
  const bandMap = Object.fromEntries(
    (catalog.all_bands || []).map((b) => [b.code, b])
  );

  const stylePrice = {};
  for (const row of catalog.style_size_stone_combinations || []) {
    const [style, shape, metal, price] = row;
    if (style === topCode && shape === shapeCode) stylePrice[metal] = parseFloat(price);
  }
  const bandPrice = {};
  for (const row of catalog.band_metal_stone_combinations || []) {
    const [band, metal, price] = row;
    if (band === bandCode) bandPrice[metal] = parseFloat(price);
  }

  const schoolEntry = (catalog.schools_data || []).find(
    (pair) => pair[0] === schoolName || pair[1]?.name === schoolName
  );
  const seals = (schoolEntry?.[1]?.variants || []).filter(
    (v) => v.supported_on?.ring !== false
  );

  /* -------------------------------------------------------------------------- */
  /* Gems / Color SKU / patterns                                                */
  /* -------------------------------------------------------------------------- */
  /** Map metafield / human labels → Mesh3D gemstone_colors values. */
  function normalizeGemColor(raw) {
    const s = String(raw || '')
      .toLowerCase()
      .trim()
      .replace(/[_-]+/g, ' ');
    if (!s || s === 'white' || s.includes('diamond') || s.includes('clear')) {
      return s === 'white' ? 'white' : '';
    }
    const exact = gemColors.find(
      (g) => g.value === s || String(g.label || '').toLowerCase() === s
    );
    if (exact) return exact.value;
    if (s.includes('purple') || s.includes('violet') || s.includes('lavender') || s.includes('amethyst')) {
      return 'purple';
    }
    if (s.includes('garnet')) return 'garnet';
    if (
      s.includes('ruby') ||
      s.includes('scarlet') ||
      s.includes('crimson') ||
      s.includes('maroon') ||
      s.includes('cardinal') ||
      s === 'red' ||
      s.includes(' red')
    ) {
      return 'ruby';
    }
    if (s.includes('emerald') || s.includes('green')) return 'green';
    if (s.includes('sapphire') || s.includes('blue')) return 'blue';
    if (s.includes('orange') || s.includes('burnt')) return 'orange';
    if (s.includes('yellow') || s.includes('gold stone') || s.includes('citrine')) {
      return 'yellow';
    }
    if (s.includes('black') || s.includes('onyx')) return 'black';
    if (s.includes('white')) return 'white';
    return '';
  }

  function normalizeAccentList(raw) {
    const list = Array.isArray(raw) ? raw : raw != null && raw !== '' ? [raw] : [];
    const out = [];
    for (const item of list) {
      // list-of-strings or list-of-objects with value/label
      const token =
        typeof item === 'string'
          ? item
          : item?.value || item?.label || item?.name || '';
      // allow "purple, white" or "red / black" in one cell
      for (const part of String(token).split(/[,/&+|]+/)) {
        const n = normalizeGemColor(part);
        if (n && n !== 'white' && !out.includes(n)) out.push(n);
      }
    }
    return out;
  }

  /** Soft school fallbacks only when accent metafields are empty (audit target). */
  function accentsFromSchoolName(name) {
    const s = String(name || '').toLowerCase();
    if (s.includes('baylor')) return ['green', 'yellow'];
    if (s.includes('tarleton')) return ['purple'];
    if (s.includes('james madison') || s.includes('jmu')) return ['purple'];
    if (s.includes('lamar')) return ['ruby'];
    if (s.includes('texas tech') || s.includes('tech university')) return ['ruby'];
    if (s.includes('texas state') || s.includes('txst') || s.includes('southwest texas')) {
      return ['ruby'];
    }
    if (s.includes('a&m') || s.includes('texas a')) return ['ruby'];
    if (s.includes('utsa') || s.includes('san antonio')) return ['orange'];
    if (s.includes('utrgv') || s.includes('rio grande')) return ['orange', 'green'];
    if (s.includes('ole miss') || s.includes('mississippi')) return ['blue'];
    if (s.includes('incarnate') || s.includes('uiw')) return ['ruby'];
    return [];
  }

  /** Parse title / stone hint when metafields are empty. Do not use product description. */
  function accentsFromTitleHints() {
    const hay = [
      recipe.stone_color_hint,
      recipe.product_title,
      recipe.top_label,
      schoolName,
    ]
      .join(' ')
      .toLowerCase();
    if (!hay.trim()) return [];
    if (/\b(alternating\s+)?red\b|\bruby\b|\bcardinal\b|\bmaroon\b|\bcrimson\b/.test(hay)) {
      return ['ruby'];
    }
    if (/\b(alternating\s+)?green\b|\bemerald\b/.test(hay)) return ['green'];
    if (/\b(alternating\s+)?purple\b|\bviolet\b|\blavender\b/.test(hay)) {
      return ['purple'];
    }
    if (/\b(alternating\s+)?orange\b/.test(hay)) return ['orange'];
    if (/\b(alternating\s+)?blue\b|\bsapphire\b/.test(hay)) return ['blue'];
    if (/\b(alternating\s+)?yellow\s+stones?\b|\byellow\b(?!\s*gold)/.test(hay)) {
      return ['yellow'];
    }
    if (/\b(alternating\s+)?black\b|\bonyx\b/.test(hay)) return ['black'];
    return [];
  }

  /**
   * Primary preset stone recipe from product metafield ``custom.color_sku``.
   * Marvin grammar + 177 double-halo rows (G1/G2 inner, G3/G4 outer):
   *   "PURPLE ALT" / "PINWHEEL" → pinwheel (alt both rows)
   *   "Red Inner"   → ruby + ALL_INNER
   *   "RED ALT OUTER" → ruby + ALT_OUTER
   *   "GAR ALT" / "blue alt x" …
   */
  function parseColorSku(raw) {
    const cm = String(raw || '').trim();
    if (!cm) return { color: '', pattern: '' };
    const up = cm.toUpperCase();
    let pattern = '';
    if (/\bPINWHEEL\b/.test(up) || /\b4\s*CORNERS?\b/.test(up)) pattern = 'PINWHEEL';
    else if (/\bALT\s*X\b/.test(up)) pattern = 'ALT_X';
    else if (/\bALT\s*INNER\b/.test(up) || (/\bALT\b/.test(up) && /\bINNER\b/.test(up))) {
      pattern = 'ALT_INNER';
    } else if (/\bALT\s*OUTER\b/.test(up) || (/\bALT\b/.test(up) && /\bOUTER\b/.test(up))) {
      pattern = 'ALT_OUTER';
    } else if (/\bINNER\b/.test(up)) pattern = 'ALL_INNER';
    else if (/\bOUTER\b/.test(up)) pattern = 'ALL_OUTER';
    else if (/\bALT\b/.test(up)) pattern = 'ALT';
    else if (/\bALL\b|\bFULL\b/.test(up)) pattern = 'ALL';
    else if (/\bX\b/.test(up)) pattern = 'X';

    let colorPart = cm
      .replace(/\bPINWHEEL\b/gi, '')
      .replace(/\b4\s*CORNERS?\b/gi, '')
      .replace(/\bALT\s*X?\b/gi, '')
      .replace(/\bINNER\b/gi, '')
      .replace(/\bOUTER\b/gi, '')
      .replace(/\bALL\b|\bFULL\b/gi, '')
      .replace(/\bX\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^[-/,]+|[-/,]+$/g, '');

    const abbrev = {
      GAR: 'garnet',
      PURP: 'purple',
      RUBY: 'ruby',
      RED: 'ruby',
      BLU: 'blue',
      ORG: 'orange',
      GRN: 'green',
      YEL: 'yellow',
      BLK: 'black',
      PURPLE: 'purple',
      GREEN: 'green',
      BLUE: 'blue',
      ORANGE: 'orange',
      YELLOW: 'yellow',
      BLACK: 'black',
      GARNET: 'garnet',
      WHITE: 'white',
    };
    const key = colorPart.toUpperCase();
    const color = abbrev[key] || normalizeGemColor(colorPart);
    return { color, pattern };
  }

  const colorSkuRaw = String(recipe.color_sku || '').trim();
  const hasColorSku = Boolean(colorSkuRaw);
  const colorSkuParsed = parseColorSku(recipe.color_sku);
  let preferredPatternKind = ''; // ALT | ALL | X | ALT_X | ''

  let accents = [];
  // Blank color_sku = photo is all-white: default the 3D to All White, but still
  // load school accents so eligible styles (193 halo, etc.) keep a stone picker.
  if (!hasColorSku) {
    preferredPatternKind = 'ALL';
  } else if (colorSkuParsed.color && colorSkuParsed.color !== 'white') {
    accents = [colorSkuParsed.color];
    preferredPatternKind = colorSkuParsed.pattern || 'ALT';
  } else if (colorSkuParsed.color === 'white') {
    preferredPatternKind = colorSkuParsed.pattern || 'ALL';
  } else if (colorSkuParsed.pattern) {
    preferredPatternKind = colorSkuParsed.pattern;
  }

  if (!accents.length) {
    accents = normalizeAccentList(recipe.accent_gem_colors);
  }
  if (hasColorSku && !accents.length) {
    accents = normalizeAccentList(recipe.stone_color_hint);
  }
  if (hasColorSku && !accents.length) {
    accents = accentsFromTitleHints();
  }
  if (!accents.length && window.SJSchoolColors) {
    accents = normalizeAccentList(window.SJSchoolColors.lookup(schoolName));
  }
  if (!accents.length) {
    accents = accentsFromSchoolName(schoolName);
  }
  // Photo contract: empty color_sku always opens All White, even if accents exist.
  const preferAllWhite = !hasColorSku || !accents.length;
  if (preferAllWhite && !preferredPatternKind) preferredPatternKind = 'ALL';

  const defaultStonePattern = String(recipe.default_stone_pattern || '').toUpperCase();
  if (defaultStonePattern === 'ALL' && accents.length) {
    preferredPatternKind = 'ALL';
  }

  function gemLabel(value) {
    return gemColors.find((g) => g.value === value)?.label || value;
  }

  /** Shared Marvin stone maps — assets/sj-stone-maps.js */
  const SM = window.SjStoneMaps;
  if (!SM) {
    console.error('[preset-3d-options] sj-stone-maps.js must load first');
  }

  function stoneMapCtx() {
    return { topCode: topCode, shapeCode: shapeCode };
  }

  function gemMapKey() {
    return SM ? SM.gemMapKey(topCode, shapeCode) : '';
  }

  function isFourCornerX() {
    return SM ? SM.isFourCornerX(topCode) : false;
  }

  function activeGemMap() {
    return SM ? SM.getGemMap(gemMapKey()) : null;
  }

  function gemsForPattern(kind, colorA, colorB) {
    if (!SM) return ['white', 'white', 'white', 'white'];
    return SM.gemsForPattern(kind, colorA, colorB, stoneMapCtx());
  }

  function buildPatterns() {
    if (!hasTopStones) return [];
    const patterns = [
      {
        id: 'all_white',
        label: 'All White',
        gems: gemsForPattern('all_white'),
      },
    ];

    const is177 = String(topCode || '').replace(/F$/i, '') === '177';
    const fourX = isFourCornerX();

    for (const accent of accents) {
      const label = gemLabel(accent);
      patterns.push({
        id: `all_${accent}`,
        label: `All ${label}`,
        gems: gemsForPattern('all', accent),
      });

      if (is177) {
        // Pinwheel / alt: A = odds (G1/G3), B = shifted evens (G2/G4)
        patterns.push({
          id: `pinwheel_${accent}`,
          label: `Pinwheel ${label}`,
          gems: gemsForPattern('pinwheel', accent),
        });
        patterns.push({
          id: `pinwheel_inner_shift_${accent}`,
          label: `Pinwheel ${label} · Flip Inner`,
          gems: gemsForPattern('pinwheel_inner_shift', accent),
        });
        patterns.push({
          id: `pinwheel_outer_shift_${accent}`,
          label: `Pinwheel ${label} · Flip Outer`,
          gems: gemsForPattern('pinwheel_outer_shift', accent),
        });
        patterns.push({
          id: `pinwheel_b_${accent}`,
          label: `Pinwheel ${label} · Shift Both`,
          gems: gemsForPattern('pinwheel_b', accent),
        });
        patterns.push({
          id: `all_inner_${accent}`,
          label: `All ${label} Inner`,
          gems: gemsForPattern('all_inner', accent),
        });
        patterns.push({
          id: `all_outer_${accent}`,
          label: `All ${label} Outer`,
          gems: gemsForPattern('all_outer', accent),
        });
        patterns.push({
          id: `alt_inner_${accent}`,
          label: `Alternating ${label} Inner`,
          gems: gemsForPattern('alt_inner', accent),
        });
        patterns.push({
          id: `alt_inner_b_${accent}`,
          label: `Alternating ${label} Inner · Shift`,
          gems: gemsForPattern('alt_inner_b', accent),
        });
        patterns.push({
          id: `alt_outer_${accent}`,
          label: `Alternating ${label} Outer`,
          gems: gemsForPattern('alt_outer', accent),
        });
        patterns.push({
          id: `alt_outer_b_${accent}`,
          label: `Alternating ${label} Outer · Shift`,
          gems: gemsForPattern('alt_outer_b', accent),
        });
      } else if (!fourX) {
        patterns.push({
          id: `alt_${accent}`,
          label: `Alternating ${label}`,
          gems: gemsForPattern('alt', accent, 'white'),
        });
      }
    }

    if (is177 && accents.length >= 2) {
      const a = accents[0];
      const b = accents[1];
      const la = gemLabel(a);
      const lb = gemLabel(b);
      patterns.push({
        id: `pinwheel_${a}_${b}`,
        label: `Pinwheel ${la} / ${lb}`,
        gems: gemsForPattern('pinwheel_2', a, b),
      });
      patterns.push({
        id: `pinwheel_inner_shift_${a}_${b}`,
        label: `Pinwheel ${la} / ${lb} · Flip Inner`,
        gems: gemsForPattern('pinwheel_2_inner_shift', a, b),
      });
      patterns.push({
        id: `pinwheel_outer_shift_${a}_${b}`,
        label: `Pinwheel ${la} / ${lb} · Flip Outer`,
        gems: gemsForPattern('pinwheel_2_outer_shift', a, b),
      });
      patterns.push({
        id: `pinwheel_b_${a}_${b}`,
        label: `Pinwheel ${la} / ${lb} · Shift Both`,
        gems: gemsForPattern('pinwheel_2b', a, b),
      });
      patterns.push({
        id: `alt_inner_${a}_${b}`,
        label: `Alternating ${la} / ${lb} Inner`,
        gems: gemsForPattern('alt_inner_2', a, b),
      });
      patterns.push({
        id: `alt_outer_${a}_${b}`,
        label: `Alternating ${la} / ${lb} Outer`,
        gems: gemsForPattern('alt_outer_2', a, b),
      });
      patterns.push({
        id: `all_inner_${a}_outer_${b}`,
        label: `All ${la} Inner / All ${lb} Outer`,
        gems: gemsForPattern('all_inner_outer', a, b),
      });
      patterns.push({
        id: `alt_inner_${a}_all_outer_${b}`,
        label: `Alt ${la} Inner / All ${lb} Outer`,
        gems: gemsForPattern('alt_inner_all_outer', a, b),
      });
      patterns.push({
        id: `all_inner_${a}_alt_outer_${b}`,
        label: `All ${la} Inner / Alt ${lb} Outer`,
        gems: gemsForPattern('all_inner_alt_outer', a, b),
      });
      // Cross swap (outer accent first in label when shopper thinks outer-first)
      patterns.push({
        id: `all_inner_${b}_outer_${a}`,
        label: `All ${lb} Inner / All ${la} Outer`,
        gems: gemsForPattern('all_inner_outer', b, a),
      });
    } else if (accents.length >= 2) {
      // Two-school-color alternating (e.g. Baylor green + yellow)
      const a = accents[0];
      const b = accents[1];
      patterns.push({
        id: `alt_${a}_${b}`,
        label: `Alternating ${gemLabel(a)} / ${gemLabel(b)}`,
        gems: gemsForPattern('alt', a, b),
      });
    }

    // X on corner groups — single-halo styles only (177 G3/G4 = outer row, not corners)
    // 310/311: no every-other / single-color X; two-color Alternating + Alternating X only.
    if (!is177) {
      const map = activeGemMap();
      const hasCorners = Boolean(map && (map.G3?.length || map.G4?.length));
      if (hasCorners || (styleMeta.gemstone_groups_count || 0) >= 4) {
        if (!fourX) {
          for (const accent of accents) {
            patterns.push({
              id: `x_${accent}`,
              label: `X — ${gemLabel(accent)}`,
              gems: gemsForPattern('x', accent),
            });
          }
        }
        if (accents.length >= 2) {
          const a = accents[0];
          const b = accents[1];
          patterns.push({
            id: `alt_x_${a}_${b}`,
            label: `Alternating X — ${gemLabel(a)} / ${gemLabel(b)}`,
            gems: gemsForPattern('alt_x', a, b),
          });
        }
      }
    }

    return patterns;
  }

  const patterns = buildPatterns();
  const patternField = root.querySelector('[data-field="pattern"]');
  if (patternField) {
    if (hasTopStones) patternField.removeAttribute('hidden');
    else patternField.setAttribute('hidden', '');
  }

  const els = {
    seal: root.querySelector('[data-preset-seal]'),
    sealShape: root.querySelector('[data-preset-seal-shape]'),
    band: root.querySelector('[data-preset-band]'),
    sealMount: root.querySelector('[data-preset-seal-mount]'),
    bandMount: root.querySelector('[data-preset-band-mount]'),
    metalChips: root.querySelector('[data-preset-metal-chips]'),
    metalHost: root.querySelector('[data-preset-metal-host]'),
    sealColorChips: root.querySelector('[data-preset-seal-color-chips]'),
    ringColorChips: root.querySelector('[data-preset-ring-color-chips]'),
    logoColorChips: root.querySelector('[data-preset-logo-color-chips]'),
    engChipBtn: root.querySelector('[data-preset-engraving-chip]'),
    engChipSub: root.querySelector('[data-preset-engraving-chip-sub]'),
    engHost: root.querySelector('[data-preset-engraving-host]'),
    sealColor: root.querySelector('[data-preset-seal-color]'),
    metal: root.querySelector('[data-preset-metal]'),
    ringColor: root.querySelector('[data-preset-ring-color]'),
    logoColor: root.querySelector('[data-preset-logo-color]'),
    twoTone: root.querySelector('[data-preset-two-tone]'),
    twoToneWrap: root.querySelector('[data-preset-two-tone-wrap]'),
    logoColorWrap: root.querySelector('[data-preset-logo-color-wrap]'),
    pattern: root.querySelector('[data-preset-pattern]'),
    patternLabel: root.querySelector('[data-preset-pattern-label]'),
    patternPrev: root.querySelector('[data-preset-pattern-prev]'),
    patternNext: root.querySelector('[data-preset-pattern-next]'),
    size: root.querySelector('[data-preset-size]'),
    engToggle: root.querySelector('[data-preset-engraving-toggle]'),
    engFields: root.querySelector('[data-engraving-fields]'),
    engText: root.querySelector('[data-preset-engraving]'),
    engCode: root.querySelector('[data-preset-engraving-code]'),
    engApply: root.querySelector('[data-preset-engraving-apply]'),
    engStatus: root.querySelector('[data-preset-engraving-status]'),
    engCount: root.querySelector('[data-engraving-count]'),
    engPrice: root.querySelector('[data-engraving-price]'),
    engMeta: root.querySelector('[data-sj-engraving-meta]'),
    price: root.querySelector('[data-preset-price]'),
    atc: root.querySelector('[data-preset-atc]'),
  };

  const variantMatrix =
    root.dataset.variantMatrix === 'true' || recipe.variant_matrix === true;
  let matrixVariants = [];
  if (variantMatrix) {
    const vEl = root.querySelector('[data-preset-3d-variants]');
    try {
      matrixVariants = vEl ? JSON.parse(vEl.textContent) : [];
    } catch (e) {
      console.error('[preset-3d] bad variants JSON', e);
      matrixVariants = [];
    }
  }

  const SEAL_SHAPE_MAP = {
    'Official 10mm': { code: 'TAR', shape: 'N', twoToneOk: true },
    'Official 12mm': { code: 'TAR', shape: 'E', twoToneOk: true },
    'Star 10mm': { code: 'TARSTAR', shape: 'N', twoToneOk: false },
    'Star 12mm': { code: 'TARSTAR', shape: 'E', twoToneOk: false },
    'Star 12x10': { code: 'TARSTAR', shape: 'L', twoToneOk: false },
  };
  const BAND_LABEL_TO_CODE = {
    'Single Shank': 'SS',
    'Stones On Shank': 'SD',
    'Infinity Shank': 'IF',
    'Light Criss-Cross Shank': 'LC',
    'Heavy Criss-Cross Shank': 'HC',
    'Criss-Cross W/ Stones': 'CD',
    'Twisted Wire Shank': 'SR',
    'V Twisted Wire Shank': 'VR',
    'V Shank': 'VS',
    'V Shank W/Stones': 'VD',
    'Cigar Shank': 'CG',
    Integrated: '',
  };
  const BAND_CODE_TO_LABEL = Object.fromEntries(
    Object.entries(BAND_LABEL_TO_CODE)
      .filter(([, code]) => code)
      .map(([label, code]) => [code, label])
  );

  /** Metafield "Single Shank Twisted Wire" / SSR → Twisted Wire Shank, not Single Shank. */
  function bandOptionFromShank(raw) {
    const s = String(raw || '').trim();
    if (!s) return '';
    if (BAND_LABEL_TO_CODE[s]) return s;
    const l = s.toLowerCase();
    if (l.includes('stones on shank') || l.includes('sos')) return 'Stones On Shank';
    if (l.includes('v twisted') || l === 'vr') return 'V Twisted Wire Shank';
    if (l.includes('twisted') || l.includes('ssr') || l === 'sr') return 'Twisted Wire Shank';
    if (l.includes('v shank') && l.includes('stone')) return 'V Shank W/Stones';
    if (l.includes('v shank') || l === 'vs') return 'V Shank';
    if (l.includes('single shank')) return 'Single Shank';
    if (l.includes('infinity')) return 'Infinity Shank';
    if (l.includes('light criss')) return 'Light Criss-Cross Shank';
    if (l.includes('heavy criss')) return 'Heavy Criss-Cross Shank';
    if (l.includes('criss')) return 'Criss-Cross W/ Stones';
    if (l.includes('cigar')) return 'Cigar Shank';
    return BAND_CODE_TO_LABEL[s.toUpperCase()] || '';
  }
  const METAL_ALIAS = {
    'Platinum Silver CZ': 'Platinum Silver / CZ',
    '10K Moissanite': '10K / Moissanite',
    '14K Lab Diamond': '14K / Diamond - Lab Grown',
  };

  function matrixSealMeta() {
    return (
      SEAL_SHAPE_MAP[els.sealShape?.value] ||
      SEAL_SHAPE_MAP['Official 10mm']
    );
  }

  function findMatrixVariant() {
    const sealShape = els.sealShape?.value || '';
    const band = els.band?.value || '';
    const metal = els.metal?.value || '';
    return (
      matrixVariants.find(
        (v) =>
          v.seal_shape === sealShape &&
          v.band === band &&
          v.metal_stone === metal
      ) || null
    );
  }

  function syncMatrixTwoToneUi() {
    if (!variantMatrix) return;
    const ok = Boolean(matrixSealMeta().twoToneOk);
    if (els.twoToneWrap) els.twoToneWrap.hidden = !ok;
    if (!ok && els.twoTone) els.twoTone.checked = false;
    const on = ok && Boolean(els.twoTone?.checked);
    if (els.logoColorWrap) els.logoColorWrap.hidden = !on;
  }

  /* -------------------------------------------------------------------------- */
  /* Options UI helpers                                                         */
  /* -------------------------------------------------------------------------- */
  function fillSelect(select, options, selected) {
    if (!select) return;
    select.innerHTML = '';
    for (const opt of options) {
      const o = document.createElement('option');
      o.value = opt.value;
      o.textContent = opt.label;
      if (opt.value === selected) o.selected = true;
      select.appendChild(o);
    }
  }

  function syncOptionLabels() {
    root.querySelectorAll('[data-field]').forEach((field) => {
      const select = field.querySelector('select');
      const label = field.querySelector('[data-option-value]');
      if (select && label) {
        const t = select.options[select.selectedIndex]?.text || '';
        label.textContent = t;
      }
    });
  }

  function mapMetalColorLabel(raw) {
    const s = String(raw || '').toLowerCase().trim();
    if (!s || s.includes('select') || s.includes('your own') || s.includes('choice')) {
      return '';
    }
    if (s.includes('yellow')) return 'yellow';
    if (s.includes('rose') || s.includes('pink')) return 'rose';
    if (
      s.includes('white') ||
      s.includes('silver') ||
      s.includes('platinum') ||
      s === 'pl silver'
    ) {
      return 'white';
    }
    return '';
  }

  function mapSealColorLabel(raw) {
    let s = String(raw || '').toLowerCase().trim();
    if (!s || s.includes('select') || s.includes('your own') || s.includes('choice')) {
      return '';
    }
    if (s.includes('w/')) {
      s = s.split('w/')[0].trim();
    }
    if (s.includes('yellow')) return 'yellow';
    if (s.includes('rose') || s.includes('pink')) return 'rose';
    if (s.includes('pl-silver') || s === 'pl silver') return 'pl-silver';
    if (s.includes('platinum') && s.includes('silver') && !s.includes('white')) {
      return 'pl-silver';
    }
    if (s.includes('white') || (s.includes('silver') && !s.includes('platinum'))) {
      return 'white';
    }
    if (s.includes('platinum')) return 'pl-silver';
    return '';
  }

  function mapLogoColorLabel(raw) {
    const s = String(raw || '').toLowerCase().trim();
    if (s.includes('w/')) {
      return mapSealColorLabel(s.split('w/')[1]);
    }
    return '';
  }

  function restoreQuery() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (_) {
      return new URLSearchParams();
    }
  }

  function pickSelect(select, wanted) {
    if (!select || wanted == null || wanted === '') return false;
    const w = String(wanted).trim();
    const opts = [...select.options];
    const hit =
      opts.find((o) => o.value === w) ||
      opts.find((o) => o.value.toLowerCase() === w.toLowerCase()) ||
      opts.find((o) => (o.textContent || '').trim().toLowerCase() === w.toLowerCase());
    if (!hit) return false;
    select.value = hit.value;
    return true;
  }

  function applyRestoreFromUrl() {
    const q = restoreQuery();
    if (![...q.keys()].some((k) => k !== 'variant' && k !== 'portal_version')) {
      return false;
    }
    let applied = false;

    const bandRaw = q.get('band_style') || q.get('band_code') || '';
    if (bandRaw && els.band) {
      const label = BAND_LABEL_TO_CODE[bandRaw]
        ? bandRaw
        : BAND_CODE_TO_LABEL[String(bandRaw).toUpperCase()] ||
          bandOptionFromShank(bandRaw);
      if (pickSelect(els.band, label || bandRaw)) applied = true;
    }

    const metalRaw = q.get('metal_stone') || '';
    if (metalRaw && els.metal) {
      const fromAlias = Object.entries(METAL_ALIAS).find(
        ([ui, sheet]) => ui === metalRaw || sheet === metalRaw
      );
      if (
        pickSelect(els.metal, metalRaw) ||
        (fromAlias && pickSelect(els.metal, fromAlias[0]))
      ) {
        applied = true;
      }
    }

    const sealShape = q.get('seal_shape') || '';
    if (sealShape && els.sealShape && pickSelect(els.sealShape, sealShape)) {
      applied = true;
    } else if (els.sealShape) {
      const shape = q.get('item_shape') || '';
      const p1 = String(q.get('p1code') || '').toUpperCase();
      if (shape) {
        const wantStar = p1.includes('STAR');
        const hit = [...els.sealShape.options].find((o) => {
          const meta = SEAL_SHAPE_MAP[o.value];
          return (
            meta &&
            meta.shape === shape &&
            (wantStar ? meta.code === 'TARSTAR' : meta.code === 'TAR')
          );
        });
        if (hit) {
          els.sealShape.value = hit.value;
          applied = true;
        }
      }
    }

    if (els.size && pickSelect(els.size, q.get('ring_size'))) applied = true;

    if (typeof syncSealLogoColorOptions === 'function') syncSealLogoColorOptions();
    const sealColor = mapSealColorLabel(q.get('seal_color') || q.get('p2color'));
    if (
      sealColor &&
      els.sealColor &&
      [...els.sealColor.options].some((o) => o.value === sealColor)
    ) {
      els.sealColor.value = sealColor;
      applied = true;
    }

    if (q.get('is2tone') === 'true' && els.twoTone) {
      els.twoTone.checked = true;
      applied = true;
      if (typeof syncSealLogoColorOptions === 'function') syncSealLogoColorOptions();
    }

    const logoColor = mapSealColorLabel(q.get('logo_color') || q.get('p3color'));
    if (
      logoColor &&
      els.logoColor &&
      [...els.logoColor.options].some((o) => o.value === logoColor)
    ) {
      els.logoColor.value = logoColor;
      applied = true;
    }

    const engraving = q.get('engraving');
    if (engraving && els.engText) {
      const text = String(engraving)
        .replace(/\s*\|\s*/g, '\n')
        .split('\n')
        .slice(0, 3)
        .map((line) => line.substring(0, 6).replaceAll('  ', ' ').replaceAll('|', ''))
        .join('\n');
      if (els.engToggle) els.engToggle.checked = true;
      els.engText.value = text;
      applied = true;
    }

    return applied;
  }

  function resolveMatrixLogoColor(ringColor) {
    const fromSelect = els.logoColor?.value;
    if (fromSelect) return fromSelect;
    const fromRecipe = mapSealColorLabel(recipe.default_logo_color);
    if (fromRecipe) return fromRecipe;
    return ringColor === 'yellow' ? 'white' : 'yellow';
  }

  /** Seal on PS: free Platinum Silver + paid 14K upgrades. */
  function sealColorChoices(ps) {
    if (ps) {
      return [
        { value: 'pl-silver', label: 'Platinum Silver' },
        { value: 'white', label: '14K White Gold' },
        { value: 'yellow', label: '14K Yellow Gold' },
        { value: 'rose', label: '14K Rose Gold' },
      ];
    }
    return (metalColors || []).map((c) => ({
      value: c.value,
      label: c.label,
    }));
  }

  /**
   * Logo on PS two-tone: 14K White/Yellow/Rose only (no Platinum Silver),
   * and no seal-color surcharge — logo metal is included with two-tone.
   */
  function logoColorChoices(ps) {
    if (ps) {
      return [
        { value: 'white', label: '14K White Gold' },
        { value: 'yellow', label: '14K Yellow Gold' },
        { value: 'rose', label: '14K Rose Gold' },
      ];
    }
    return (metalColors || []).map((c) => ({
      value: c.value,
      label: c.label,
    }));
  }

  function sealColorSurcharge(colorValue, metalStone) {
    if (!isPlatinumSilver(metalStone)) return 0;
    if (!colorValue || colorValue === 'pl-silver') return 0;
    const key =
      metalStone ||
      'Platinum Silver / CZ';
    const surcharges =
      sealSurchargeMap[key] ||
      sealSurchargeMap['Platinum Silver / CZ'] ||
      {};
    const n = parseFloat(surcharges[colorValue]);
    return Number.isFinite(n) ? n : 0;
  }

  // Variant prices already include the pictured seal (14K yellow/rose/white).
  // Only charge the difference when the shopper changes seal color.
  function extraSealSurcharge(colorValue, metalStone) {
    const baked = mapSealColorLabel(recipe.default_seal_color);
    return (
      sealColorSurcharge(colorValue, metalStone) -
      sealColorSurcharge(baked, metalStone)
    );
  }

  function formatPlusDollars(amount) {
    const n = Number(amount) || 0;
    if (!n) return '';
    try {
      return (
        '+' +
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(n)
      );
    } catch (_) {
      return '+$' + Math.round(n);
    }
  }

  function syncSealLogoColorOptions() {
    const ps = isPlatinumSilver(els.metal?.value);
    const pick = (selectEl, choices, preferred, fallback) => {
      if (!selectEl || !choices.length) return;
      let cur = String(selectEl.value || '');
      // When on PS, if cur was 'white' (from gold ring white-metal option), map to 'pl-silver'
      if (ps && cur === 'white') {
        cur = 'pl-silver';
      }
      const next = choices.some((c) => c.value === cur)
        ? cur
        : choices.some((c) => c.value === preferred)
          ? preferred
          : fallback || choices[0].value;
      fillSelect(selectEl, choices, next);
    };
    const sealChoices = sealColorChoices(ps);
    let sealPref = mapSealColorLabel(recipe.default_seal_color);
    if (ps) {
      if (!sealPref || sealPref === 'white' || sealPref === 'pl-silver') {
        sealPref = 'pl-silver';
      }
    } else if (!sealPref) {
      sealPref = 'white';
    }
    pick(els.sealColor, sealChoices, sealPref, ps ? 'pl-silver' : 'white');
    if (els.logoColor) {
      const logoChoices = logoColorChoices(ps);
      // PS logo: default yellow contrast (no pl-silver option)
      let logoPref =
        mapSealColorLabel(recipe.default_logo_color) ||
        mapLogoColorLabel(recipe.default_seal_color);
      if (ps && (logoPref === 'pl-silver' || !logoPref)) logoPref = 'yellow';
      if (!logoPref) logoPref = 'yellow';
      pick(els.logoColor, logoChoices, logoPref, ps ? 'yellow' : 'white');
    }
  }

  function resolveDefaultSealCode() {
    const raw = String(recipe.default_seal_design || '').trim();
    if (!raw || !seals.length) return seals[0]?.code;
    const needle = raw.toLowerCase();
    const byCode = seals.find((s) => String(s.code || '').toLowerCase() === needle);
    if (byCode) return byCode.code;
    const byName = seals.find((s) =>
      String(s.name || '')
        .toLowerCase()
        .includes(needle)
    );
    if (byName) return byName.code;
    // handle / metafield often "BU Seal" while catalog code is "BU"
    const compact = needle.replace(/[^a-z0-9]/g, '');
    const byCompact = seals.find((s) => {
      const code = String(s.code || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      const name = String(s.name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      return code === compact || name.includes(compact) || compact.includes(code);
    });
    return byCompact?.code || seals[0]?.code;
  }

  // Metal / stone options available for this top+shape (+ band when used)
  const metalOptions = (catalog.metal_stone_combinations || [])
    .filter(
      (m) =>
        stylePrice[m] != null && (waivesBand || !bandCode || bandPrice[m] != null)
    )
    .map((m) => ({ value: m, label: m }));

  function resolveDefaultMetalStone() {
    const wanted = String(recipe.default_metal_stone || '').trim();
    if (wanted) {
      const exact = metalOptions.find((m) => m.value === wanted);
      if (exact) return exact.value;
    }
    const metalType = String(recipe.metal_type || wanted || '').toLowerCase();
    const pick = (pred) => metalOptions.find((m) => pred(m.value))?.value;
    if (metalType.includes('platinum') || metalType.includes('silver')) {
      return pick((v) => v.includes('Platinum Silver')) || metalOptions[0]?.value;
    }
    if (metalType.includes('10')) {
      return (
        pick((v) => v.startsWith('10K') && v.includes('Moissanite')) ||
        pick((v) => v.startsWith('10K')) ||
        metalOptions[0]?.value
      );
    }
    if (metalType.includes('14') || metalType.includes('gold')) {
      return (
        pick((v) => v.startsWith('14K') && v.includes('Moissanite')) ||
        pick((v) => v.startsWith('14K')) ||
        metalOptions[0]?.value
      );
    }
    return (
      pick((v) => v === wanted) ||
      pick((v) => v.includes('Platinum Silver')) ||
      metalOptions[0]?.value
    );
  }

  function colorFromMetalType() {
    const metalType = String(recipe.metal_type || recipe.default_metal_stone || '').toLowerCase();
    if (metalType.includes('rose')) return 'rose';
    if (metalType.includes('white') && metalType.includes('gold')) return 'white';
    if (
      metalType.includes('gold') ||
      metalType.includes('14') ||
      metalType.includes('10')
    ) {
      if (
        metalType.includes('platinum') ||
        metalType.includes('silver') ||
        metalType.includes('white')
      ) {
        return 'white';
      }
      return 'yellow';
    }
    return 'white';
  }

  const defaultMetal = resolveDefaultMetalStone();
  // Put the recipe default first so the dropdown reads gold-first on gold presets
  if (defaultMetal) {
    metalOptions.sort((a, b) => {
      if (a.value === defaultMetal) return -1;
      if (b.value === defaultMetal) return 1;
      return 0;
    });
  }

  const lookFromMetal = colorFromMetalType();
  const defaultRingColor =
    mapMetalColorLabel(recipe.default_ring_color) || lookFromMetal || 'white';
  let defaultSealColor = mapSealColorLabel(recipe.default_seal_color);
  const isPsOpener =
    isPlatinumSilver(defaultMetal) || isPlatinumSilver(els.metal?.value);
  if (isPsOpener) {
    if (
      !defaultSealColor ||
      defaultSealColor === 'white' ||
      defaultSealColor === 'pl-silver'
    ) {
      defaultSealColor = 'pl-silver';
    }
  } else if (!defaultSealColor) {
    const rawSeal = String(recipe.default_seal_color || '').toLowerCase();
    if (
      /platinum/.test(rawSeal) ||
      (/silver/.test(rawSeal) && !/white|yellow|rose/.test(rawSeal))
    ) {
      defaultSealColor = 'pl-silver';
    } else {
      defaultSealColor = lookFromMetal || 'white';
    }
  }
  const defaultSealCode = resolveDefaultSealCode();

  if (!variantMatrix) {
    fillSelect(
      els.seal,
      seals.map((s) => ({ value: s.code, label: s.name })),
      defaultSealCode
    );
  }
  if (!variantMatrix) {
    fillSelect(els.metal, metalOptions, defaultMetal);
  }
  fillSelect(
    els.ringColor,
    metalColors.map((c) => ({ value: c.value, label: c.label })),
    defaultRingColor
  );
  // Seal + logo options depend on metal (PS → pl-silver + paid 14K upgrades)
  syncSealLogoColorOptions();
  if (
    els.sealColor &&
    defaultSealColor &&
    [...els.sealColor.options].some((o) => o.value === defaultSealColor)
  ) {
    els.sealColor.value = defaultSealColor;
  }
  if (variantMatrix && els.twoTone) {
    const wantTwoTone =
      recipe.two_tone === true ||
      recipe.two_tone === 'true' ||
      root.dataset.twoTone === 'true';
    if (wantTwoTone) els.twoTone.checked = true;
  }
  syncMatrixTwoToneUi();
  if (variantMatrix && els.twoTone?.checked) {
    syncSealLogoColorOptions();
    const logoPref = mapSealColorLabel(recipe.default_logo_color);
    if (
      logoPref &&
      els.logoColor &&
      [...els.logoColor.options].some((o) => o.value === logoPref)
    ) {
      els.logoColor.value = logoPref;
    }
  }
  applyRestoreFromUrl();

  /* -------------------------------------------------------------------------- */
  /* Matrix UI — Customizable Baylor seal strip + band dropdown + size chips    */
  /* -------------------------------------------------------------------------- */
  function initMatrixBuilderUi() {
    if (!variantMatrix || typeof window.SjImageStepper !== 'function') return;

    applyRestoreFromUrl();
    const defaultBandLabel = bandOptionFromShank(
      recipe.default_band_label || recipe.band_label || ''
    );
    const restoreBand = restoreQuery().get('band_style') || restoreQuery().get('band_code');
    if (defaultBandLabel && els.band && !restoreBand) {
      const match = [...els.band.options].find(
        (o) =>
          o.value === defaultBandLabel ||
          String(o.value || '').toLowerCase() === defaultBandLabel.toLowerCase()
      );
      if (match) els.band.value = match.value;
    }

    const sealImages =
      (window.__SJ_IMAGE_STEPPER_CONFIG__ &&
        window.__SJ_IMAGE_STEPPER_CONFIG__.sealImages) ||
      {};
    const bandImages =
      (window.__SJ_IMAGE_STEPPER_CONFIG__ &&
        window.__SJ_IMAGE_STEPPER_CONFIG__.bandImages) ||
      {};

    const DESIGN_META = [
      {
        code: 'TAR',
        label: 'Official',
        shapes: ['N', 'E'],
        twoToneOk: true,
      },
      {
        code: 'TARSTAR',
        label: 'Star',
        shapes: ['N', 'E', 'L'],
        twoToneOk: false,
      },
    ];
    const SHAPE_TO_LABEL = { N: '10mm', E: '12mm', L: '12x10' };
    const SIZE_CHIP = {
      N: { title: '10mm', subtitle: 'Small round' },
      E: { title: '12mm', subtitle: 'Large round' },
      L: { title: '12×10mm', subtitle: 'Oval' },
    };

    function parseSealShapeValue(raw) {
      const v = String(raw || '');
      const m = v.match(/^(Official|Star)\s+(10mm|12mm|12x10)$/i);
      if (!m) return { design: 'Official', shape: 'N' };
      const design = m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase();
      const shape =
        /12x10/i.test(m[2]) ? 'L' : /12mm/i.test(m[2]) ? 'E' : 'N';
      return { design: design === 'Star' ? 'Star' : 'Official', shape };
    }

    function composeSealShapeValue(design, shape) {
      return `${design} ${SHAPE_TO_LABEL[shape] || '10mm'}`;
    }

    function sealImageFor(code, shape) {
      const key = `${code}${shape}`.toUpperCase();
      return sealImages[key] || sealImages[code] || '';
    }

    function moneyTwoTone() {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(twoTonePrice);
      } catch (_) {
        return `$${twoTonePrice}`;
      }
    }

    let sealDesign = parseSealShapeValue(els.sealShape?.value).design;
    let sealShapeLetter = parseSealShapeValue(els.sealShape?.value).shape;
    let sealStepper = null;
    let bandStepper = null;

    function availableDesigns() {
      return DESIGN_META.filter((d) =>
        d.shapes.some((sh) =>
          [...(els.sealShape?.options || [])].some(
            (o) => o.value === composeSealShapeValue(d.label, sh)
          )
        )
      );
    }

    function availableShapesForDesign(designLabel) {
      const meta = DESIGN_META.find((d) => d.label === designLabel);
      if (!meta) return [];
      return meta.shapes.filter((sh) =>
        [...(els.sealShape?.options || [])].some(
          (o) => o.value === composeSealShapeValue(designLabel, sh)
        )
      );
    }

    /** Union of all seal sizes on the product (oval shows even on Official). */
    function allAvailableShapes() {
      const order = ['N', 'E', 'L'];
      const seen = new Set();
      [...(els.sealShape?.options || [])].forEach((o) => {
        const meta = SEAL_SHAPE_MAP[o.value];
        if (meta && meta.shape) seen.add(meta.shape);
      });
      return order.filter((sh) => seen.has(sh));
    }

    function designOptions(shapeLetter) {
      return availableDesigns()
        .filter((d) => d.shapes.includes(shapeLetter))
        .map((d) => ({
          value: d.label,
          label: d.label,
          imageUrl: sealImageFor(d.code, shapeLetter),
        }));
    }

    function setSealShapeSelect(design, shape) {
      const next = composeSealShapeValue(design, shape);
      if (!els.sealShape) return;
      const ok = [...els.sealShape.options].some((o) => o.value === next);
      if (!ok) return;
      if (els.sealShape.value !== next) {
        els.sealShape.value = next;
        els.sealShape.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    function resolveDesignForShape(letter, preferred) {
      const onPreferred = availableShapesForDesign(preferred);
      if (onPreferred.includes(letter)) return preferred;
      const designs = designOptions(letter);
      return designs[0] ? designs[0].value : preferred;
    }

    function renderSealChips() {
      if (!sealStepper || typeof sealStepper.footerExtraEl !== 'function') return;
      const host = sealStepper.footerExtraEl();
      if (!host) return;
      const meta = DESIGN_META.find((d) => d.label === sealDesign);
      const shapes = allAvailableShapes();
      const twoToneOk = Boolean(meta && meta.twoToneOk);
      host.innerHTML = '';
      if (sealStepper.root) {
        sealStepper.root.setAttribute('data-seal-size', sealShapeLetter || '');
      }

      const wrap = document.createElement('div');
      wrap.className = 'sj-seal-size-chips';
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-label', 'Seal size and finish');

      // Size chips + Two-tone on the right (same chip size as 10/12)
      shapes.forEach((letter) => {
        const copy = SIZE_CHIP[letter] || { title: letter, subtitle: '' };
        const onThisDesign = availableShapesForDesign(sealDesign).includes(letter);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sj-seal-size-chip';
        btn.classList.toggle('is-selected', letter === sealShapeLetter);
        if (!onThisDesign) btn.classList.add('is-soft');
        btn.setAttribute('data-seal-size', letter);
        btn.setAttribute('aria-pressed', letter === sealShapeLetter ? 'true' : 'false');
        btn.title = onThisDesign
          ? copy.title
          : `${copy.title} (switches seal design)`;
        btn.innerHTML =
          `<span class="sj-seal-size-chip__text">` +
          `<span class="sj-seal-size-chip__title">${copy.title}</span>` +
          (copy.subtitle
            ? `<span class="sj-seal-size-chip__subtitle">${copy.subtitle}</span>`
            : '') +
          `</span>`;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          sealShapeLetter = letter;
          sealDesign = resolveDesignForShape(letter, sealDesign);
          setSealShapeSelect(sealDesign, sealShapeLetter);
          if (sealStepper) {
            sealStepper.setOptions(designOptions(sealShapeLetter));
            sealStepper.syncFromValue(sealDesign, { keepWindow: true });
          }
          renderSealChips();
          refresh();
        });
        wrap.appendChild(btn);
      });

      if (twoToneOk) {
        const on = Boolean(els.twoTone?.checked);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sj-seal-size-chip sj-seal-size-chip--two-tone';
        btn.classList.toggle('is-selected', on);
        btn.classList.toggle('is-on', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const price = moneyTwoTone();
        btn.setAttribute(
          'aria-label',
          `Two-tone seal: ${on ? 'Yes' : 'Off'}${on ? `, ${price}` : ''}`
        );
        btn.innerHTML =
          `<span class="sj-seal-size-chip__text">` +
          `<span class="sj-seal-size-chip__title">Two-tone</span>` +
          `<span class="sj-seal-size-chip__subtitle">${
            on ? `Yes · ${price}` : 'Off'
          }</span>` +
          `</span>`;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (els.twoTone) {
            els.twoTone.checked = !els.twoTone.checked;
            els.twoTone.dispatchEvent(new Event('change', { bubbles: true }));
          }
          syncSealLogoColorOptions();
          renderSealChips();
          renderMatrixColorChips();
          refresh();
        });
        wrap.appendChild(btn);
      }

      host.appendChild(wrap);
      if (els.twoToneWrap) {
        els.twoToneWrap.classList.add('sj-seal-size-absorbed');
        els.twoToneWrap.hidden = false;
      }
    }

    // Same chip chrome as 10mm / Two-tone (sj-seal-size-chip).
    // Option value stays "Platinum Silver CZ" so the variant still matches.
    // Shoppers see Moissanite. Every variant-matrix preset uses this table.
    const METAL_CHIP_COPY = {
      'Platinum Silver CZ': {
        title: 'Platinum Silver',
        stone: 'Moissanite',
        tagline: 'Bright & Tarnish-Resistant',
      },
      '10K Moissanite': {
        title: '10K Gold',
        stone: 'Moissanite',
        tagline: 'Strength & Sparkle',
      },
      '14K Lab Diamond': {
        title: '14K Gold',
        stone: 'Lab Grown Diamonds',
        tagline: 'Luxurious and Timeless',
      },
    };

    function renderMatrixMetalChips() {
      if (!els.metalChips || !els.metal) return;
      const current = String(els.metal.value || '');
      if (els.metalHost) {
        els.metalHost.classList.add('sj-metal-chips-host', 'is-enhanced');
      }
      els.metalChips.className = 'sj-seal-size-chips sj-metal-size-chips';
      els.metalChips.setAttribute('role', 'group');
      els.metalChips.setAttribute('aria-label', 'Metal / Stone');
      els.metalChips.innerHTML = '';
      [...els.metal.options]
        .filter((o) => o.value)
        .forEach((opt) => {
          const copy = METAL_CHIP_COPY[opt.value] || {
            title: opt.value,
            stone: '',
            tagline: '',
          };
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'sj-seal-size-chip sj-metal-size-chip';
          btn.classList.toggle('is-selected', opt.value === current);
          btn.setAttribute('aria-pressed', opt.value === current ? 'true' : 'false');
          btn.setAttribute(
            'aria-label',
            [copy.title, copy.stone, copy.tagline].filter(Boolean).join(', ')
          );
          btn.innerHTML =
            `<span class="sj-seal-size-chip__text">` +
            `<span class="sj-seal-size-chip__title">${copy.title}</span>` +
            (copy.stone
              ? `<span class="sj-seal-size-chip__subtitle">${copy.stone}</span>`
              : '') +
            (copy.tagline
              ? `<span class="sj-seal-size-chip__tagline">${copy.tagline}</span>`
              : '') +
            `</span>`;
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            if (els.metal.value !== opt.value) {
              els.metal.value = opt.value;
              els.metal.dispatchEvent(new Event('change', { bubbles: true }));
            }
            syncSealLogoColorOptions();
            renderMatrixMetalChips();
            renderMatrixColorChips();
            refresh();
          });
          els.metalChips.appendChild(btn);
        });
    }

    function matrixSwatchMap() {
      const m =
        window.__SJ_IMAGE_STEPPER_CONFIG__ &&
        window.__SJ_IMAGE_STEPPER_CONFIG__.metalSwatches;
      return m && typeof m === 'object' ? m : {};
    }

    function matrixColorKey(value, label) {
      const v = String(value || '').toLowerCase();
      const t = String(label || '');
      if (v === 'yellow' || /yellow/i.test(t)) return 'yellow';
      if (v === 'rose' || /rose/i.test(t)) return 'rose';
      if (v === 'pl-silver' || /pl\s*silver|platinum/i.test(t)) return 'pl-silver';
      if (v === 'white' || /white/i.test(t)) return 'white';
      return v || '';
    }

    function matrixColorCaption(kind, value) {
      const key = matrixColorKey(value, '');
      if (kind === 'ring') {
        if (isPlatinumSilver(els.metal?.value)) return 'Platinum Silver';
        const metal = String(els.metal?.value || '');
        const karat = /^14/i.test(metal) ? '14K' : /^10/i.test(metal) ? '10K' : '';
        const gold =
          key === 'yellow'
            ? 'Yellow Gold'
            : key === 'rose'
              ? 'Rose Gold'
              : key === 'white'
                ? 'White Gold'
                : '';
        if (karat && gold) return karat + ' ' + gold;
        return gold || value || '';
      }
      // Seal / logo — always 14K gold labels (builder), except PS
      if (key === 'pl-silver') return 'Platinum Silver';
      if (key === 'white') return '14K White Gold';
      if (key === 'yellow') return '14K Yellow Gold';
      if (key === 'rose') return '14K Rose Gold';
      return value || '';
    }

    /**
     * Builder-style color panels (preset-scoped classes).
     * PS seal/logo: 4 labeled chips (PS free + 14K White/Yellow/Rose +$200).
     * Otherwise Seal/Ring/Logo = compact horizontal swatches.
     * @param {{ lockPs?: boolean }} [opts]
     */
    function renderOneMatrixColorRow(host, selectEl, visible, kind, opts) {
      if (!host) return;
      const field = host.closest('[data-field]');
      if (!selectEl || !visible) {
        host.innerHTML = '';
        host.hidden = true;
        if (field) field.classList.add('preset-3d-swatch-host');
        return;
      }
      host.hidden = false;
      if (field) {
        field.classList.add('preset-3d-swatch-host');
        field.classList.remove('sj-color-chips-host');
        field.hidden = false;
      }

      const lockPs = Boolean(opts && opts.lockPs);
      const metalUi = els.metal?.value || '';
      const metalStone = METAL_ALIAS[metalUi] || metalUi;
      const psMetal = isPlatinumSilver(metalUi);
      // PS seal: named chips + surcharge. PS logo: named 14K chips, no surcharge.
      const labeledPs =
        psMetal && (kind === 'seal' || kind === 'logo') && !lockPs;
      // +$200 only in the header (never under each chip — avoids double stack).
      const showHeaderPrice = labeledPs && kind === 'seal';
      const title =
        kind === 'seal' ? 'Seal Color' : kind === 'logo' ? 'Logo Color' : 'Ring Color';
      const selectedPrice = showHeaderPrice
        ? formatPlusDollars(extraSealSurcharge(selectEl.value, metalStone))
        : '';
      const summary = lockPs
        ? 'Platinum Silver'
        : matrixColorCaption(kind, selectEl.value) +
          (selectedPrice ? ' ' + selectedPrice : '');
      const map = matrixSwatchMap();
      const current = lockPs ? 'white' : String(selectEl.value || '');
      if (lockPs && els.ringColor) els.ringColor.value = 'white';

      const panel = document.createElement('div');
      panel.className =
        'preset-3d-swatch-panel' +
        (labeledPs ? ' is-labeled' : ' is-compact') +
        ' preset-3d-swatch-panel--' +
        kind;
      panel.setAttribute('role', 'group');
      panel.setAttribute('aria-label', title);

      const head = document.createElement('div');
      head.className = 'preset-3d-swatch-panel__head';
      head.innerHTML =
        '<span class="preset-3d-swatch-panel__title">' +
        title +
        (summary ? ':' : '') +
        '</span>' +
        (summary
          ? '<span class="preset-3d-swatch-panel__summary">' + summary + '</span>'
          : '');

      const wrap = document.createElement('div');
      wrap.className =
        'preset-3d-swatches is-row' + (labeledPs ? ' is-labeled-row' : '');

      let optsList = [...selectEl.options].filter((o) => o.value);
      if (lockPs) {
        optsList = [{ value: 'white', textContent: 'Platinum Silver' }];
      }

      optsList.forEach((opt) => {
        const key = lockPs
          ? 'pl-silver'
          : matrixColorKey(opt.value, opt.textContent);
        const caption = lockPs
          ? 'Platinum Silver'
          : matrixColorCaption(kind, opt.value);
        const chipSurcharge =
          showHeaderPrice && !lockPs
            ? formatPlusDollars(extraSealSurcharge(opt.value, metalStone))
            : '';
        const imgUrl = (key && map[key]) || map.white || '';
        const hex =
          key === 'yellow'
            ? '#D4AF37'
            : key === 'rose'
              ? '#B76E79'
              : key === 'pl-silver'
                ? '#C0C0C0'
                : '#E8E8E8';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className =
          'preset-3d-swatch' + (labeledPs ? ' is-labeled' : ' is-chip');
        if (key === 'pl-silver') btn.classList.add('is-pl-silver');
        if (key === 'white' && !lockPs) btn.classList.add('is-white-gold');
        btn.classList.toggle('is-selected', opt.value === current || lockPs);
        btn.setAttribute(
          'aria-pressed',
          opt.value === current || lockPs ? 'true' : 'false'
        );
        btn.setAttribute(
          'aria-label',
          caption + (chipSurcharge ? ', ' + chipSurcharge : '')
        );
        if (lockPs) btn.disabled = true;
        const swatchHtml = imgUrl
          ? `<img class="preset-3d-swatch__img" src="${imgUrl}" alt="" width="40" height="40" loading="lazy" decoding="async">`
          : `<span class="preset-3d-swatch__dot" style="background-color:${hex}"></span>`;
        if (labeledPs) {
          // Labels only under chips — surcharge lives in the panel header.
          btn.innerHTML =
            swatchHtml +
            '<span class="preset-3d-swatch__text">' +
            `<span class="preset-3d-swatch__label">${caption}</span>` +
            '</span>';
        } else {
          btn.innerHTML = swatchHtml;
        }
        if (!lockPs) {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            if (selectEl.value !== opt.value) {
              selectEl.value = opt.value;
              selectEl.dispatchEvent(new Event('change', { bubbles: true }));
            }
            renderMatrixColorChips();
            refresh();
          });
        }
        wrap.appendChild(btn);
      });

      panel.appendChild(head);
      panel.appendChild(wrap);
      host.innerHTML = '';
      host.appendChild(panel);
    }

    function renderMatrixColorChips() {
      const ps = isPlatinumSilver(els.metal?.value);
      const twoToneOn =
        Boolean(matrixSealMeta().twoToneOk) && Boolean(els.twoTone?.checked);
      // PS: seal only. 10K/14K: Ring | Seal side-by-side.
      renderOneMatrixColorRow(
        els.ringColorChips,
        els.ringColor,
        !ps && Boolean(els.ringColor),
        'ring'
      );
      renderOneMatrixColorRow(els.sealColorChips, els.sealColor, true, 'seal');
      renderOneMatrixColorRow(
        els.logoColorChips,
        els.logoColor,
        twoToneOn && Boolean(els.logoColor),
        'logo'
      );
      const sealHost = colorField('seal_color');
      const ringHost = colorField('ring_color');
      if (ringHost) {
        ringHost.hidden = ps;
        if (ps && els.ringColor) els.ringColor.value = 'white';
      }
      if (sealHost) sealHost.classList.toggle('is-ps-labeled', ps);
      if (els.logoColorWrap) {
        els.logoColorWrap.hidden = !twoToneOn;
        els.logoColorWrap.classList.toggle('is-ps-labeled', ps && twoToneOn);
      }
    }

    function syncEngravingChip() {
      if (!els.engChipBtn) return;
      const on = Boolean(els.engToggle?.checked);
      els.engChipBtn.classList.toggle('is-selected', on);
      els.engChipBtn.classList.toggle('is-on', on);
      els.engChipBtn.classList.toggle('is-free', on && engravingWaived);
      els.engChipBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      paintEngravingChip();
      if (els.engFields) els.engFields.hidden = !on;
      if (els.engHost) {
        els.engHost.classList.toggle('is-open', on);
      }
    }

    function bindEngravingChip() {
      if (!els.engChipBtn || !els.engToggle || els.engChipBtn._presetBound) return;
      els.engChipBtn._presetBound = true;
      els.engChipBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        els.engToggle.checked = !els.engToggle.checked;
        els.engToggle.dispatchEvent(new Event('change', { bubbles: true }));
        syncEngravingChip();
        if (els.engToggle.checked && els.engText) {
          try {
            els.engText.focus();
          } catch (_) {}
        }
      });
      syncEngravingChip();
    }

    if (els.sealMount && els.sealShape) {
      const shapes0 = availableShapesForDesign(sealDesign);
      if (shapes0.length && !shapes0.includes(sealShapeLetter)) {
        sealShapeLetter = shapes0[0];
      }
      setSealShapeSelect(sealDesign, sealShapeLetter);
      sealStepper = new window.SjImageStepper({
        mountEl: els.sealMount,
        kind: 'seal',
        layout: 'strip',
        visibleCount: 2,
        ariaLabel: 'Seal Design',
        options: designOptions(sealShapeLetter),
        getValue: () => sealDesign,
        setValue: (value) => {
          sealDesign = value;
          const shapes = availableShapesForDesign(sealDesign);
          if (!shapes.includes(sealShapeLetter) && shapes[0]) {
            sealShapeLetter = shapes[0];
          }
          setSealShapeSelect(sealDesign, sealShapeLetter);
          renderSealChips();
          refresh();
        },
      });
      renderSealChips();
    }

    if (els.bandMount && els.band) {
      const bandOpts = [...els.band.options]
        .filter((o) => o.value)
        .map((o) => {
          const code = BAND_LABEL_TO_CODE[o.value] || '';
          const img =
            bandImages[code] ||
            bandImages[code.toUpperCase()] ||
            bandImages[o.value] ||
            '';
          return { value: o.value, label: o.textContent.trim() || o.value, imageUrl: img };
        });
      bandStepper = new window.SjImageStepper({
        mountEl: els.bandMount,
        kind: 'band',
        layout: 'strip',
        visibleCount: 4,
        ariaLabel: 'Band Style',
        emptyLabel: 'Select band',
        options: bandOpts,
        getValue: () => String(els.band.value || ''),
        setValue: (value) => {
          if (els.band.value !== value) {
            els.band.value = value;
            els.band.dispatchEvent(new Event('change', { bubbles: true }));
          }
          refresh();
        },
      });
      bandStepper.syncFromValue(els.band.value);
    }

    renderMatrixMetalChips();
    renderMatrixColorChips();
    bindEngravingChip();
    root._presetMatrixUi = {
      renderMetal: renderMatrixMetalChips,
      renderColors: renderMatrixColorChips,
      syncEngraving: syncEngravingChip,
      renderSealChips: renderSealChips,
    };
    refresh();
  }

  // Defer until SjImageStepper script (deferred) is ready
  function whenStepperReady(tries) {
    if (typeof window.SjImageStepper === 'function') {
      initMatrixBuilderUi();
      return;
    }
    if ((tries || 0) > 40) return;
    window.setTimeout(() => whenStepperReady((tries || 0) + 1), 50);
  }
  if (variantMatrix) whenStepperReady(0);

  // Default Pattern from Color SKU (ALT / ALL / INNER / OUTER / PINWHEEL / X)
  let patternIndex = 0;
  function findPatternId(id) {
    return patterns.findIndex((p) => p.id === id);
  }
  const is177Style = String(topCode || '').replace(/F$/i, '') === '177';
  if (!preferAllWhite && accents.length) {
    const c = accents[0];
    const kind = preferredPatternKind || 'ALT';
    if (kind === 'ALL') patternIndex = findPatternId(`all_${c}`);
    else if (kind === 'ALL_INNER') patternIndex = findPatternId(`all_inner_${c}`);
    else if (kind === 'ALL_OUTER') patternIndex = findPatternId(`all_outer_${c}`);
    else if (kind === 'ALT_INNER') patternIndex = findPatternId(`alt_inner_${c}`);
    else if (kind === 'ALT_OUTER') patternIndex = findPatternId(`alt_outer_${c}`);
    else if (kind === 'PINWHEEL') {
      patternIndex =
        accents.length >= 2
          ? findPatternId(`pinwheel_${accents[0]}_${accents[1]}`)
          : findPatternId(`pinwheel_${c}`);
      if (patternIndex < 0) patternIndex = findPatternId(`pinwheel_${c}`);
    } else if (kind === 'X') patternIndex = findPatternId(`x_${c}`);
    else if (kind === 'ALT_X' && accents.length >= 2) {
      patternIndex = patterns.findIndex(
        (p) => p.id.startsWith('alt_x_') || p.id.startsWith('pinwheel_')
      );
    } else if (is177Style && (kind === 'ALT' || !kind)) {
      // 177 Color SKU "RED ALT" → alternating inner
      patternIndex = findPatternId(`alt_inner_${c}`);
      if (patternIndex < 0) patternIndex = findPatternId(`pinwheel_${c}`);
    } else {
      patternIndex = findPatternId(`alt_${c}`);
    }
    if (patternIndex < 0 && (kind === 'ALL_INNER' || kind === 'ALT_INNER')) {
      patterns.push({
        id: `all_inner_${c}`,
        label: `All ${gemLabel(c)} Inner`,
        gems: gemsForPattern('all_inner', c),
      });
      patternIndex = findPatternId(`all_inner_${c}`);
    }
    if (patternIndex < 0) {
      patternIndex = patterns.findIndex(
        (p) =>
          p.id.startsWith('pinwheel_') ||
          (p.id.startsWith('alt_') &&
            !p.id.includes('_x_') &&
            p.id.split('_').length === 2)
      );
    }
  }
  if (patternIndex < 0) {
    patternIndex = findPatternId('all_white');
  }
  if (patternIndex < 0) patternIndex = 0;
  (function restoreGemsFromUrl() {
    const gems = ['p1g1', 'p1g2', 'p1g3', 'p1g4']
      .map((k) => restoreQuery().get(k))
      .filter(Boolean);
    if (!gems.length || !patterns.length) return;
    const idx = patterns.findIndex((p) => {
      const g = p.gems || [];
      return gems.every(
        (c, i) =>
          !g[i] || String(g[i]).toLowerCase() === String(c).toLowerCase()
      );
    });
    if (idx >= 0) patternIndex = idx;
  })();

  function setPatternIndex(next, { silent } = {}) {
    if (!patterns.length) return;
    const len = patterns.length;
    // Wrap infinitely: past last → first, before first → last
    patternIndex = ((next % len) + len) % len;
    const current = patterns[patternIndex];
    if (els.pattern) els.pattern.value = current.id;
    if (els.patternLabel) els.patternLabel.textContent = current.label;
    if (els.patternPrev) els.patternPrev.disabled = len <= 1;
    if (els.patternNext) els.patternNext.disabled = len <= 1;
    if (!silent) refresh();
  }

  setPatternIndex(patternIndex, { silent: true });

  fillSelect(
    els.size,
    [
      { value: '', label: '' },
      ...ringSizes.map((s) => ({
        value: String(s),
        label: String(s),
      })),
    ],
    ''
  );

  if (els.engPrice) els.engPrice.textContent = `(+${money.format(engravingPrice)})`;

  function isPlatinumSilver(metalStone) {
    return String(metalStone || '').includes('Platinum Silver');
  }

  function colorField(name) {
    return root.querySelector(`[data-field="${name}"]`);
  }

  function countSelectableOptions(select) {
    if (!select) return 0;
    return [...select.options].filter((o) => !o.disabled && o.value !== '').length;
  }

  /**
   * Hide dropdowns when there's nothing to choose (single locked value still
   * applies to Mesh3D + cart properties). Ring Size stays visible — it always
   * needs an explicit pick (placeholder doesn't count).
   */
  function syncSingleOptionVisibility() {
    const ps = isPlatinumSilver(els.metal?.value);
    const specs = [
      { name: 'seal', select: els.seal },
      { name: 'seal_color', select: els.sealColor },
      { name: 'metal', select: els.metal },
      { name: 'ring_color', select: els.ringColor },
    ];
    for (const { name, select } of specs) {
      const field = colorField(name);
      if (!field || !select) continue;
      // Matrix: Seal/Ring color chips stay visible (builder layout) — don't collapse them.
      if (variantMatrix && (name === 'seal_color' || name === 'ring_color' || name === 'metal')) {
        continue;
      }
      if (name === 'ring_color' && ps) {
        field.hidden = true;
        continue;
      }
      field.hidden = countSelectableOptions(select) <= 1;
    }
    if (patternField) {
      // No top stones (249 / 222) → hide. Halo styles keep the picker even when
      // the photo defaults to All White (patterns include school accents).
      patternField.hidden = !hasTopStones || patterns.length <= 1;
    }
  }

  /**
   * Presets: PS body keeps ring/band white — hide Ring Color on matrix too.
   * 10K/14K show Ring | Seal side-by-side.
   */
  function syncColorFieldsForMetal() {
    const ps = isPlatinumSilver(els.metal?.value);
    const ringField = colorField('ring_color');

    if (ringField) ringField.hidden = ps;
    if (ps && els.ringColor) els.ringColor.value = 'white';
  }

  function meshColor(value) {
    // Mesh3D has no pl-silver channel — map included PS seal to white look
    if (value === 'pl-silver') return 'white';
    return value || 'white';
  }

  /** Seal disc background — portal uses flat{color}; legacy viewer uses matte{color}. */
  function sealBackgroundTone2(sealColor, twoTone) {
    const color = meshColor(sealColor);
    if (portalVersion === '2') return `flat${color}`;
    if (twoTone) return color;
    return `matte${color}`;
  }

  function colorLabel(value, metalStone, role) {
    if (role === 'ring' && isPlatinumSilver(metalStone)) return 'Platinum Silver';
    if (role === 'seal' || role === 'logo') {
      if (value === 'pl-silver') return 'Platinum Silver';
      if (isPlatinumSilver(metalStone)) {
        if (value === 'white') return '14K White Gold';
        if (value === 'yellow') return '14K Yellow Gold';
        if (value === 'rose') return '14K Rose Gold';
      }
    }
    return metalColors.find((c) => c.value === value)?.label || value;
  }

  function clampEngraving(raw, { trimTrailingEmpty } = {}) {
    const maxLines = 3;
    const maxChars = 6;
    const lines = String(raw || '')
      .split('\n')
      .slice(0, maxLines)
      .map((line) => line.substring(0, maxChars).replaceAll('  ', ' ').replaceAll('|', ''));
    // Keep trailing newlines while typing so Enter can open the next line.
    // Only strip empty trailing lines when building cart properties.
    if (trimTrailingEmpty) {
      while (lines.length && lines[lines.length - 1] === '') lines.pop();
    }
    return lines.join('\n');
  }

  function currentPattern() {
    if (!hasTopStones || !patterns.length) return { id: 'none', label: '', gems: [] };
    return patterns[patternIndex] || patterns[0];
  }

  function sealShapeOk(seal) {
    return (seal.shapes || []).some((s) => s.value === shapeCode);
  }

  function getState() {
    // Variant-matrix presets: Seal Shape / Band / Metal Stone drive Shopify variants.
    if (variantMatrix) {
      const meta = matrixSealMeta();
      shapeCode = meta.shape;
      bandCode = BAND_LABEL_TO_CODE[els.band?.value] || bandCode || 'SS';
      const metalUi = els.metal?.value || 'Platinum Silver CZ';
      const metalStone = METAL_ALIAS[metalUi] || metalUi;
      const ps = isPlatinumSilver(metalStone);
      const ringColor = ps ? 'white' : els.ringColor?.value || 'white';
      const sealColor = els.sealColor?.value || (ps ? 'pl-silver' : 'white');
      const twoToneOk = Boolean(meta.twoToneOk);
      const twoTone = twoToneOk && Boolean(els.twoTone?.checked);
      const logoColor = twoTone ? resolveMatrixLogoColor(ringColor) : ringColor;
      const pattern = currentPattern();
      const engravingOn = Boolean(els.engToggle?.checked);
      const engraving = engravingOn
        ? clampEngraving(els.engText?.value, { trimTrailingEmpty: true })
        : '';
      const lines = engraving ? engraving.split('\n').filter((l) => l.length) : [];
      const part1 = `R${topCode}${shapeCode}`;
      const sealSku = `${meta.code}${shapeCode}`;
      const part2 = twoTone ? `2${sealSku}` : sealSku;
      const part3 =
        waivesBand || !bandCode ? '' : `${topCode}${bandCode}${shapeCode}`;
      const part4 = twoTone ? `2L${sealSku}` : '';
      const p1c = meshColor(ringColor);
      const p2c = meshColor(sealColor);
      const p3c = meshColor(ringColor);
      const p4c = meshColor(logoColor);
      const gems = hasTopStones
        ? pattern?.gems || ['white', 'white', 'white', 'white']
        : [];
      let src = `${BASE}&part1=${part1}&part2=${part2}`;
      if (part3) src += `&part3=${part3}`;
      if (part4) src += `&part4=${part4}`;
      src += `&part1Color=${p1c}&part2Color=${p2c}`;
      if (part3) src += `&part3Color=${p3c}`;
      if (part4) {
        src += `&part4Color=${p4c}&sealTone2=${p4c}`;
        if (portalVersion !== '2') src += `&sealTwoTone=1`;
      }
      src += `&part2tone2=${sealBackgroundTone2(sealColor, twoTone)}`;
      gems.forEach((g, i) => {
        src += `&part1g${i + 1}=${g}`;
      });
      const matched = findMatrixVariant();
      let total = matched ? Number(matched.price) || 0 : 0;
      // PS seal gold upgrades (+$200). Logo on PS two-tone is included (no logo surcharge).
      total += extraSealSurcharge(sealColor, metalStone);
      total = applyTwoToneToTotal(total, twoTone, twoToneOk);
      if (engravingOn && engraving && !engravingWaived) total += engravingPrice;
      const sealSizeLabel =
        shapeCode === 'L' ? '12x10 mm' : shapeCode === 'E' ? '12 mm' : '10 mm';
      return {
        seal: { code: meta.code, name: els.sealShape?.value || meta.code },
        metalStone,
        metalUi,
        ringColor,
        sealColor,
        logoColor,
        ringColorLabel: colorLabel(ringColor, metalStone, 'ring'),
        sealColorLabel: colorLabel(sealColor, metalStone, 'seal'),
        pattern,
        gems,
        size: els.size?.value || '',
        engraving,
        lines,
        engravingOn,
        part1,
        part2,
        part3,
        part4,
        p1c,
        p2c,
        p3c,
        p4c,
        src,
        total,
        sealSizeLabel,
        twoTone,
        matrixVariantId: matched ? matched.id : null,
        matrixBasePrice: matched ? Number(matched.price) || 0 : 0,
      };
    }

    const seal = seals.find((s) => s.code === els.seal.value) || seals[0];
    const metalStone = els.metal.value;
    const ps = isPlatinumSilver(metalStone);
    // PS body: ring/band stay PL Silver; seal follows the seal-color control
    // (metafield can author Yellow Gold seal on silver presets).
    const ringColor = ps ? 'white' : els.ringColor.value;
    const sealColor = els.sealColor?.value || (ps ? 'pl-silver' : 'white');
    const pattern = currentPattern();
    const engravingOn = Boolean(els.engToggle?.checked);
    const engraving = engravingOn
      ? clampEngraving(els.engText?.value, { trimTrailingEmpty: true })
      : '';
    const lines = engraving ? engraving.split('\n').filter((l) => l.length) : [];

    const shapeCfg = (seal?.shapes || []).find((s) => s.value === shapeCode);
    // Match Thinkspace/kiosk: single-piece seal unless product opts into two-tone
    // (metafield/title) or this seal/shape requires two-tone only.
    const recipeTwoTone =
      recipe.two_tone === true ||
      recipe.two_tone === 'true' ||
      root.dataset.twoTone === 'true' ||
      /^2/i.test(String(recipe.seal_sku || '').replace(/\s+/g, '')) ||
      /two[\s-]*tone|2[\s-]*tone/i.test(
        `${recipe.default_seal_design || ''} ${recipe.product_title || ''} ${recipe.top_label || ''}`
      );
    const twoTone =
      recipeTwoTone || shapeCfg?.twoTone === 'only';

    // Thinkspace: p2 = code+shape; if two-tone → p2=2{code+shape}, p4=2L{code+shape}
    // Total rings (71 / SIG): omit part3 entirely — band is built into the top.
    const part1 = `R${topCode}${shapeCode}`;
    const sealSku = `${seal.code}${shapeCode}`;
    const part2 = twoTone ? `2${sealSku}` : sealSku;
    const part3 = waivesBand || !bandCode ? '' : `${topCode}${bandCode}${shapeCode}`;
    const part4 = twoTone ? `2L${sealSku}` : '';

    const p1c = meshColor(ringColor);
    const p2c = meshColor(sealColor);
    const p3c = meshColor(ringColor);

    const gems = hasTopStones
      ? pattern?.gems || ['white', 'white', 'white', 'white']
      : [];

    let src = `${BASE}&part1=${part1}&part2=${part2}`;
    if (part3) src += `&part3=${part3}`;
    if (part4) src += `&part4=${part4}`;
    src += `&part1Color=${p1c}&part2Color=${p2c}`;
    if (part3) src += `&part3Color=${p3c}`;
    if (part4) {
      // Logo metal follows ring color on presets (same default as builder logo tone)
      src += `&part4Color=${p1c}&sealTone2=${p1c}`;
      if (portalVersion !== '2') src += `&sealTwoTone=1`;
    }
    src += `&part2tone2=${sealBackgroundTone2(sealColor, twoTone)}`;
    gems.forEach((g, i) => {
      src += `&part1g${i + 1}=${g}`;
    });

    let total = stylePrice[metalStone] || 0;
    if (!waivesBand && bandCode) {
      total += bandPrice[metalStone] || 0;
    }
    // PS: pl-silver seal is free; white/yellow/rose are paid 14K upgrades
    total += sealColorSurcharge(sealColor, metalStone);
    if (twoTone) total += twoTonePrice;
    if (engravingOn && engraving && !engravingWaived) total += engravingPrice;

    const sealSizeLabel =
      shapeCode === 'L' ? '12x10 mm' : shapeCode === 'E' ? '12 mm' : '10 mm';

    const ringColorLabel = colorLabel(ringColor, metalStone, 'ring');
    const sealColorLabel = colorLabel(sealColor, metalStone, 'seal');

    return {
      seal,
      metalStone,
      ringColor,
      sealColor,
      ringColorLabel,
      sealColorLabel,
      pattern,
      gems,
      size: els.size.value,
      engraving,
      lines,
      engravingOn,
      part1,
      part2,
      part3,
      part4,
      p1c,
      p2c,
      p3c,
      src,
      total,
      sealSizeLabel,
      twoTone,
    };
  }

  /* -------------------------------------------------------------------------- */
  /* Mesh3D viewer sync                                                         */
  /* -------------------------------------------------------------------------- */
  function allViewerIframes() {
    const id = `3dviewer_${root.dataset.productId}`;
    return [...document.querySelectorAll(`[id="${id}"]`)];
  }

  function iframeOnScreen(el) {
    const r = el.getBoundingClientRect();
    if (r.width < 8 || r.height < 8) return false;
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
  }

  function findIframes() {
    const all = allViewerIframes();
    if (!all.length) return [];
    // Desktop CSS forces swiper transform:none, so the original slide is
    // visible while Swiper's .swiper-slide-active is often a hidden clone.
    // Always include originals; add on-screen duplicates for mobile loop.
    const originals = all.filter((el) => !el.closest('.swiper-slide-duplicate'));
    const visibleDups = all.filter(
      (el) => el.closest('.swiper-slide-duplicate') && iframeOnScreen(el)
    );
    const picked = [...originals, ...visibleDups];
    if (picked.length) return picked;
    const visible = all.filter(iframeOnScreen);
    if (visible.length) return visible;
    const below = all.filter((el) => el.closest('.below-mobile'));
    return below.length ? below : all;
  }

  function findIframe() {
    return findIframes()[0] || null;
  }

  const PART_SLOTS = ['part1', 'part2', 'part3', 'part4'];

  let lastAppliedSrc = '';
  let viewerReady = false;

  function bindViewerLoad() {
    allViewerIframes().forEach((iframe) => {
      if (iframe._preset3dLoadBound) return;
      iframe._preset3dLoadBound = true;
      iframe.addEventListener('load', () => iframe.classList.add('loaded'));
    });
  }

  function lowercasePartSlugs(url) {
    PART_SLOTS.forEach((key) => {
      const v = url.searchParams.get(key);
      if (v) url.searchParams.set(key, v.toLowerCase());
    });
    return url;
  }

  function portalSrc(src) {
    try {
      const u = new URL(src, window.location.href);
      if (portalVersion === '2') lowercasePartSlugs(u);
      return u.toString();
    } catch (_) {
      return src;
    }
  }

  function didPartSlotsChange(prevSrc, nextSrc) {
    if (!prevSrc) return true;
    try {
      const prev = new URL(prevSrc, window.location.href);
      const next = new URL(nextSrc, window.location.href);
      return PART_SLOTS.some((key) => {
        const a = (prev.searchParams.get(key) || '').toLowerCase();
        const b = (next.searchParams.get(key) || '').toLowerCase();
        return a !== b;
      });
    } catch (_) {
      return true;
    }
  }

  /** Mesh3D postMessage payload — matches kiosk buildViewerPayloadFromUrl / Thinkspace. */
  function buildViewerPayload(state) {
    const part = (sku) =>
      portalVersion === '2' ? String(sku || '').toLowerCase() : String(sku || '');
    const gems = {};
    (state.gems || []).forEach((g, i) => {
      gems[`g${i + 1}`] = g;
    });
    const parts = {
      part1: {
        part: part(state.part1),
        colors: { tone1: state.p1c, gems },
      },
      // Same shape as kiosk / Thinkspace transform: tone1 = seal metal,
      // tone2 = flat{color} disc. Do not send a sibling part2.color.
      part2: {
        part: part(state.part2),
        colors: {
          tone1: state.p2c,
          tone2: sealBackgroundTone2(state.sealColor, state.twoTone),
        },
      },
      // Explicitly clear band when style waives it (Fierce / Signet total rings)
      part3: state.part3
        ? {
            part: part(state.part3),
            colors: { tone1: state.p3c },
          }
        : { part: '' },
      part4: state.part4
        ? {
            part: part(state.part4),
            colors: { tone1: state.p4c || state.p1c },
          }
        : { part: '' },
    };
    const payload = { autorotate: false, zoom: true, parts };
    if (state.part4) payload.sealTone2 = state.p4c || state.p1c;
    return payload;
  }

  function postToViewer(iframe, data) {
    if (!iframe?.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(data, '*');
    } catch (err) {
      console.warn('[preset-3d] postMessage failed', err);
    }
  }

  function setIframeSrc(iframe, src) {
    const applied = portalSrc(src);
    const prev = portalSrc(iframe.getAttribute('data-src') || iframe.src || '');
    iframe.setAttribute('data-src', applied);
    if (prev !== applied) {
      iframe.classList.remove('loaded');
      iframe.src = applied;
    } else if (!iframe.getAttribute('src')) {
      iframe.src = applied;
    }
    return applied;
  }

  function updateViewer(state) {
    const iframes = findIframes();
    if (!iframes.length) {
      console.warn('[preset-3d] no Mesh3D iframe found');
      return;
    }

    const applied = portalSrc(state.src);
    const needsReload =
      !viewerReady || didPartSlotsChange(lastAppliedSrc, state.src);
    const ready = iframes.some((iframe) => iframe.classList.contains('loaded'));

    // First paint and part-SKU changes: Mesh3D reads part2tone2 from the URL.
    // Customizable rings and kiosk do not postMessage after a src reload.
    if (needsReload || !ready) {
      iframes.forEach((iframe) => setIframeSrc(iframe, state.src));
      lastAppliedSrc = applied;
      viewerReady = true;
      return;
    }

    const payload = buildViewerPayload(state);
    iframes.forEach((iframe) => {
      iframe.setAttribute('data-src', applied);
      postToViewer(iframe, payload);
      postToViewer(iframe, { type: 'assembleItems' });
    });
    lastAppliedSrc = applied;
  }

  function updatePrice(state) {
    const formatted = money.format(state.total);
    if (els.price) els.price.textContent = formatted;
    // Keep product header price in sync with builder total (live builder pattern)
    document
      .querySelectorAll(
        '.product__details .product__price [data-price], .product__details [data-product-price], .product__block--product_header [data-price], .product__price'
      )
      .forEach((n) => {
        if (n.matches('[data-price], [data-product-price]')) {
          n.textContent = formatted;
        } else if (n.classList.contains('product__price')) {
          const leaf = n.querySelector('[data-price]') || n;
          leaf.textContent = formatted;
        }
      });
    const mobilePrice = ensurePresetMobilePrice()?.querySelector(
      '[data-preset-mobile-price]'
    );
    if (mobilePrice) mobilePrice.textContent = formatted;
  }

  let presetMobilePriceBar = null;

  function presetHeroSection() {
    return document.querySelector(
      '.main-section--product[data-preset-3d-hero="true"]'
    );
  }

  function setupPresetViewer() {
    if (!variantMatrix) return;
    ensurePresetMobilePrice();
  }

  function ensurePresetMobilePrice() {
    if (!variantMatrix) return null;
    const mediaItem = presetHeroSection()?.querySelector(
      '.product__media-item:has(x-3d-viewer-thinkspace)'
    );
    if (!mediaItem) return null;
    if (presetMobilePriceBar?.isConnected) return presetMobilePriceBar;
    presetMobilePriceBar = mediaItem.querySelector(
      '[data-preset-mobile-price-bar]'
    );
    if (!presetMobilePriceBar) {
      presetMobilePriceBar = document.createElement('div');
      presetMobilePriceBar.className = 'preset-3d-mobile-price-bar';
      presetMobilePriceBar.setAttribute('data-preset-mobile-price-bar', '');
      presetMobilePriceBar.innerHTML = '<span data-preset-mobile-price></span>';
      mediaItem.appendChild(presetMobilePriceBar);
    }
    return presetMobilePriceBar;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[ch]));
  }

  function hashesMatch(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return diff === 0;
  }

  function digestHex(text) {
    if (!window.crypto || !window.crypto.subtle) return Promise.resolve('');
    const data = new TextEncoder().encode(text);
    return window.crypto.subtle.digest('SHA-256', data).then((buffer) => {
      return [...new Uint8Array(buffer)]
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    });
  }

  function engravingPriceLabel() {
    return formatPlusDollars(engravingPrice) || '+$60';
  }

  function paintEngravingChip() {
    if (!els.engChipSub) return;
    const on = Boolean(els.engToggle?.checked);
    const price = engravingPriceLabel();
    els.engChipBtn?.classList.toggle('is-free', on && engravingWaived);
    if (on && engravingWaived) {
      els.engChipSub.innerHTML =
        '<span class="sj-engraving-was">' +
        escapeHtml(price) +
        '</span><span class="sj-engraving-free">Free</span>';
      return;
    }
    els.engChipSub.textContent = price;
  }

  function paintEngravingCode() {
    if (els.engApply) {
      els.engApply.textContent = engravingWaived ? 'Applied' : 'Apply';
      els.engApply.classList.toggle('is-applied', engravingWaived);
      els.engApply.setAttribute('aria-pressed', engravingWaived ? 'true' : 'false');
    }
    const status = els.engStatus;
    if (!status) return;
    status.classList.remove('is-free', 'is-invalid');
    if (!engravingWaived && engravingCodeStatus === 'invalid') {
      status.hidden = false;
      status.classList.add('is-invalid');
      status.textContent = 'That code does not apply';
      return;
    }
    status.hidden = true;
    status.textContent = '';
  }

  function setEngravingWaived(next) {
    const changed = engravingWaived !== next;
    engravingWaived = next;
    paintEngravingCode();
    if (changed) refresh();
    else paintEngravingChip();
  }

  function applyEngravingCode() {
    const raw = String(els.engCode?.value || '');
    const ticket = ++engravingCodeTicket;
    const normalized = raw.trim().toUpperCase();
    if (!normalized) {
      engravingCodeStatus = '';
      setEngravingWaived(false);
      return;
    }
    digestHex(normalized).then((hex) => {
      if (ticket !== engravingCodeTicket) return;
      const ok = ENGRAVING_WAIVER_SHA256.some((expected) => hashesMatch(hex, expected));
      engravingCodeStatus = ok ? 'free' : 'invalid';
      setEngravingWaived(ok);
    });
  }

  function updateEngravingUi() {
    const on = Boolean(els.engToggle?.checked);
    if (els.engFields) els.engFields.hidden = !on;
    if (els.engText && on) {
      const next = clampEngraving(els.engText.value);
      if (next !== els.engText.value) els.engText.value = next;
      const used = next.split('\n').filter((l) => l.length).length;
      if (els.engCount) els.engCount.textContent = `${used} / 3 lines used`;
      if (els.engMeta) {
        els.engMeta.textContent = `3 lines — 6 characters — ${used} / 3 lines used`;
      }
    }
    if (root._presetMatrixUi && typeof root._presetMatrixUi.syncEngraving === 'function') {
      root._presetMatrixUi.syncEngraving();
    } else {
      paintEngravingChip();
    }
    paintEngravingCode();
  }

  function refresh() {
    // Disable seals that don't support locked shape
    if (els.seal && !variantMatrix) {
      [...els.seal.options].forEach((opt) => {
        const seal = seals.find((s) => s.code === opt.value);
        opt.disabled = seal ? !sealShapeOk(seal) : true;
      });
      if (els.seal.selectedOptions[0]?.disabled) {
        const first = [...els.seal.options].find((o) => !o.disabled);
        if (first) els.seal.value = first.value;
      }
    }
    syncMatrixTwoToneUi();
    syncColorFieldsForMetal();
    syncSingleOptionVisibility();
    // Do NOT rebuild color/metal chip DOM here — that caused lag + layout thrash.
    // Chip UIs update themselves on metal / two-tone / color clicks.
    updateEngravingUi();
    syncOptionLabels();
    const state = getState();
    updatePrice(state);
    scheduleViewerUpdate(state);
  }

  let _viewerTimer = null;
  function scheduleViewerUpdate(state) {
    if (_viewerTimer) window.clearTimeout(_viewerTimer);
    _viewerTimer = window.setTimeout(() => {
      _viewerTimer = null;
      updateViewer(state || getState());
    }, 60);
  }

  /* -------------------------------------------------------------------------- */
  /* Cart (Thinkspace-shaped properties → Customizable variant)                 */
  /* -------------------------------------------------------------------------- */
  function propertiesFromState(state) {
    const props = {
      School: schoolName,
      'Ring Style': `${styleMeta.label || topCode} (${topCode})`,
      'Seal Size': state.sealSizeLabel,
      'Metal / Stone': state.metalStone,
      'Ring Color': state.ringColorLabel,
      'Seal Color': state.sealColorLabel,
      'Ring Size': state.size,
      _part1: `${state.part1} (${state.p1c})`,
      _part2: `${state.part2} (${state.p2c})`,
      '2-Tone': state.twoTone ? true : false,
      _3d_viewer_url: state.src,
      _preset_source: variantMatrix ? 'preset-3d-matrix' : 'preset-3d',
      _preset_product_id: root.dataset.productId || '',
    };
    if (variantMatrix) {
      props['Seal Shape'] = els.sealShape?.value || '';
      props['Band'] = els.band?.value || '';
      if (state.twoTone) {
        props['Logo Color'] = colorLabel(
          state.logoColor || 'yellow',
          state.metalStone,
          'seal'
        );
      }
      // Live Product Options ADDS _addons_price to the variant price.
      // Matrix variants already carry base retail — only send upgrade delta
      // (two-tone / seal gold / engraving). Full total would roughly double.
      const base = Number(state.matrixBasePrice) || 0;
      const deltaCents = Math.round((Number(state.total) - base) * 100);
      if (deltaCents !== 0) {
        props._addons_price = String(deltaCents);
      }
    } else {
      // Customizable builder path: variant is typically $0; LPO uses full total.
      props._addons_price = String(Math.round(state.total * 100));
    }
    if (!waivesBand && state.part3) {
      props['Band Style'] =
        (variantMatrix
          ? els.band?.value
          : bandMap[bandCode]?.label || recipe.band_label || bandCode) ||
        bandCode;
      props._band_code = bandCode;
      props._part3 = `${state.part3} (${state.p3c})`;
    } else {
      props['Band Style'] = 'Integrated';
      props._band_code = '';
      props._part3 = '';
    }
    const presetImage = recipe.preset_image || root.dataset.presetImage || '';
    if (presetImage) props._preset_image = presetImage;
    if (hasTopStones) {
      state.gems.forEach((g, i) => {
        props[`Gem group ${i + 1}`] = gemLabel(g);
      });
    }
    if (state.engravingOn && state.engraving) {
      props['Engraving '] = state.lines.join(' | ');
      state.lines.forEach((line, i) => {
        props[`_Engraving Line ${i + 1}`] = line;
      });
    }
    const restore = new URLSearchParams();
    if (variantMatrix && els.sealShape?.value) {
      restore.set('seal_shape', els.sealShape.value);
    }
    if (els.band?.value) restore.set('band_style', els.band.value);
    if (state.metalUi || state.metalStone) {
      restore.set('metal_stone', state.metalUi || state.metalStone);
    }
    if (state.size) restore.set('ring_size', state.size);
    if (state.sealColor) restore.set('seal_color', state.sealColor);
    if (state.twoTone) restore.set('is2tone', 'true');
    if (state.twoTone && state.logoColor) {
      restore.set('logo_color', state.logoColor);
    }
    if (state.engravingOn && state.engraving) {
      restore.set('engraving', state.lines.join(' | '));
    }
    (state.gems || []).forEach((g, i) => {
      if (g) restore.set(`p1g${i + 1}`, g);
    });
    const restoreQs = restore.toString();
    if (restoreQs) props._builder_qs = '?' + restoreQs;
    return props;
  }

  async function addToCart() {
    const state = getState();
    if (!state.size) {
      els.size?.focus();
      alert('Please select a ring size.');
      return;
    }
    const atcVariantId = variantMatrix
      ? String(state.matrixVariantId || '')
      : builderVariantId;
    if (!atcVariantId) {
      alert(
        variantMatrix
          ? 'Selected options are unavailable.'
          : 'Builder product unavailable.'
      );
      return;
    }
    els.atc.disabled = true;
    els.atc.setAttribute('data-loading', 'true');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(atcVariantId),
              quantity: 1,
              properties: propertiesFromState(state),
            },
          ],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Add to cart failed');
      }
      document.dispatchEvent(
        new CustomEvent('cart:item-added', { bubbles: true, detail: {} })
      );
      document.dispatchEvent(
        new CustomEvent('quick-cart:open', { bubbles: true })
      );
      // Fallback: soft navigation to cart if quick-cart doesn't open
      setTimeout(() => {
        const drawer = document.querySelector('quick-cart, [data-quick-cart]');
        if (!drawer) window.location.href = '/cart';
      }, 600);
    } catch (err) {
      console.error('[preset-3d] ATC', err);
      alert(err.message || 'Could not add to bag');
    } finally {
      els.atc.disabled = false;
      els.atc.removeAttribute('data-loading');
    }
  }

  /* -------------------------------------------------------------------------- */
  /* Bind UI + initial refresh                                                  */
  /* -------------------------------------------------------------------------- */
  [
    els.seal,
    els.sealShape,
    els.band,
    els.sealColor,
    els.metal,
    els.ringColor,
    els.logoColor,
    els.twoTone,
    els.size,
  ].forEach((el) => {
    el?.addEventListener('change', refresh);
    el?.addEventListener('input', refresh);
  });
  els.patternPrev?.addEventListener('click', () => setPatternIndex(patternIndex - 1));
  els.patternNext?.addEventListener('click', () => setPatternIndex(patternIndex + 1));
  els.engToggle?.addEventListener('change', refresh);
  els.engText?.addEventListener('input', () => {
    updateEngravingUi();
    refresh();
  });
  els.engCode?.addEventListener('input', () => {
    engravingCodeStatus = '';
    if (engravingWaived) setEngravingWaived(false);
    else paintEngravingCode();
  });
  els.engCode?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    applyEngravingCode();
  });
  els.engApply?.addEventListener('click', (event) => {
    event.preventDefault();
    applyEngravingCode();
  });
  els.atc?.addEventListener('click', addToCart);

  const productEl = root.closest('.product');
  if (productEl) {
    productEl.setAttribute('data-is-3d-product', 'true');
    productEl.setAttribute('data-is-3d-product-loaded', 'true');
  }
  if (variantMatrix) setupPresetViewer();
  bindViewerLoad();

  applyRestoreFromUrl();
  if (root._presetMatrixUi) {
    if (typeof root._presetMatrixUi.renderMetal === 'function') {
      root._presetMatrixUi.renderMetal();
    }
    if (typeof root._presetMatrixUi.renderColors === 'function') {
      root._presetMatrixUi.renderColors();
    }
    if (typeof root._presetMatrixUi.syncEngraving === 'function') {
      root._presetMatrixUi.syncEngraving();
    }
  }
  refresh();
  // Lazy iframes may mount after defer; retry ring URL briefly
  let tries = 0;
  const retry = window.setInterval(() => {
    tries += 1;
    if (variantMatrix) setupPresetViewer();
    const iframe = findIframe();
    if (iframe || tries > 20) {
      window.clearInterval(retry);
      if (iframe) refresh();
    }
  }, 250);
})();
